<i18n>
{
  "en": {
    "vip_badge": "VIP",
    "svip_badge": "SVIP",
    "annual_badge": "Annual",
    "limited_tag": "Limited Availability",
    "expired_tag": "Availability Ended",
    "future_tag": "Upcoming",
    "vr_tag": "VR",
    "rating": "User Rating",
    "release": "Release Date",
    "duration": "Duration",
    "performers": "Featuring",
    "age": "Age",
    "series": "Series",
    "tags": "Tags",
    "view_details": "View Details"
  },
  "ja": {
    "vip_badge": "VIP",
    "svip_badge": "超VIP",
    "annual_badge": "年間",
    "limited_tag": "期間限定",
    "expired_tag": "配信期間終了",
    "future_tag": "近日配信",
    "vr_tag": "VR",
    "rating": "ユーザー評価",
    "release": "配信日",
    "duration": "再生時間",
    "performers": "出演",
    "age": "年齢",
    "series": "シリーズ",
    "tags": "タグ",
    "view_details": "もっと見る"
  }
}
</i18n>

<template>
<div class="movie-info">
  <div class="movie-title">
    <div class="movie-tag">
      <!-- movie status tags -->
      <div v-if="movie.IsLimited" class="tag-status is-limited">{{ $t('limited_tag') }}</div>
      <div v-if="movie.IsExpired" class="tag-status is-expired">{{ $t('expired_tag') }}</div>
      <div v-if="movie.IsComingSoon" class="tag-status is-future">{{ $t('future_tag') }}</div>
      <div v-if="movie.isVR" class="tag-status is-vr">{{ $t('vr_tag') }}</div>
    </div>
    <h1>
      <!-- movie membership badges -->
      <span v-if="!newXPEligible && movie.isVIP" class="tag-member is-vip">{{ $t('vip_badge') }}</span>
      <span v-if="!newXPEligible && movie.isSVIP" class="tag-member is-svip">{{ $t('svip_badge') }}</span>
      <span v-if="!newXPEligible && movie.isAnnual" class="tag-member is-annual">{{ $t('annual_badge') }}</span>
      <!-- movie title -->
      <span v-if="locale == 'ja'" v-html="movie.Title"></span>
      <span v-if="locale == 'en'">
        <span v-if="movie.TitleEn != null && movie.TitleEn != ''" v-html="movie.TitleEn"></span>
        <span v-else v-html="movie.ActressesEn.join(', ')"></span>
      </span>
    </h1>
  </div>
  <!-- movie description -->
  <p v-if="locale == 'ja'" class="movie-description">{{ movie.Desc }}</p>
  <p v-if="locale == 'en' && movie.DescEn != null && movie.DescEn != ''" class="movie-description">{{ movie.DescEn }}</p>
  <!-- movie specs -->
  <ul>
    <li class="movie-spec">
      <span class="spec-title">{{ $t('performers') }}</span>
      <span class="spec-content">
        <router-link
         v-for="(props, actressId) in movie.ActressesList"
         :key="actressId"
         :to="`/search/?a=${actressId}`"
         class="spec-item">{{ (locale == 'en') ? props.NameEn : props.NameJa }}</router-link>
      </span>
    </li>
    <li class="movie-spec" v-if="!hideReleaseDate">
      <span class="spec-title">{{ $t('release') }}</span>
      <span class="spec-content">{{ movie.ReleaseDate }}<span v-if="movie.ExpireDate"> &ndash; {{ movie.ExpireDate }} <span v-if="!movie.IsExpired">{{ movie.ExpireTime }}</span></span></span>
    </li>
    <li class="movie-spec">
      <span class="spec-title">{{ $t('duration') }}</span>
      <span class="spec-content">{{ movie.DurationFmt }}</span>
    </li>
    <li class="movie-spec">
      <span class="spec-title">{{ $t('tags') }}</span>
      <span class="spec-content">
        <!-- 2022/02: skip s/vip for the new-experience -->
        <router-link
          v-for="(props, UC) in categoryList"
          :key="UC"
          :to="`/search/?c=${UC}`"
          class="spec-item">{{ (locale === 'ja') ? movie.UcNameList[UC].NameJa : movie.UcNameList[UC].NameEn }}</router-link>
      </span>
    </li>
  </ul>
</div>
</template>

<script>
/* eslint max-len: 0 */

import BifrostAPI from '@/assets/js/services/Bifrost/API';
import MovieHelper from '@/assets/js/utils/movie';

export default {
  props: {
    hideReleaseDate: Boolean,
  },
  data() {
    return {
      movie: {},
      movieKey: '',
      categoryMap: {},
    };
  },
  async mounted() {
    const bfAPI = new BifrostAPI();

    const movie = await bfAPI.getMovieDetail(this.$route.params.movieId);
    this.movie = MovieHelper.expandDetails(movie);
    if (this.movie.MetaMovieID && this.userIsSiteMember) {
      bfAPI.ReqMovieKeys(this.movie.MetaMovieID).then((movieKeys) => {
        this.movieKey = movieKeys.KeyMercury;
      });
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale;
    },
    newXPEligible() {
      return this.$store.getters['user/isNewXPEligible'];
    },
    userIsSiteMember() {
      return this.$store.getters['user/isSiteMember'];
    },
    categoryList() {
      // show categories, depending on if the user is new XP eligible; if they are, don't show the
      // tier categories (VIP, SVIP, Annual)
      const HIDDEN_CATS_FOR_CLASSIC = ['82', '83'];
      const HIDDEN_CATS_FOR_NEW_XP = ['82', '83', '50000', '50001', '50003'];
      const categoryIDs = this.movie.UcNameList ? Object.keys(this.movie.UcNameList)
        .filter(id => ((!this.newXPEligible && !HIDDEN_CATS_FOR_CLASSIC.includes(id)) || (this.newXPEligible && !HIDDEN_CATS_FOR_NEW_XP.includes(id))))
        .reduce((obj, key) => Object.assign(obj, { [key]: this.movie.UcNameList[key] }), {}) : null;
      return categoryIDs;
    },
  },
  watch: {
    async userIsSiteMember(newVal) {
      if (newVal) {
        // logged in; get movie keys
        const bfAPI = new BifrostAPI();
        const movieKeys = await bfAPI.ReqMovieKeys(this.movie.MetaMovieID);
        this.movieKey = movieKeys.KeyMercury;
      } else {
        // logged out; clear movie keys
        this.movieKey = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/sass/pages/_detail.scss';
</style>
